// "Rompiendo el techo" — Blog de páginas planas (sin CMS).
//
// Cada artículo vive en su propia URL RAÍZ (no bajo /rompiendo-el-techo/), definida
// explícitamente en /app/frontend/src/App.js.
//
// Campos:
// - number: identificador interno 1-10 (usado para asociar la imagen /blog-images/{number}.jpeg)
// - slug: define la URL final -> https://garcin.cl/{slug} (sin barra final, consistente con el resto del sitio)
// - image: ruta de la imagen del artículo (mismo número que el documento fuente)
// - category: eyebrow/etiqueta temática
// - title: título exacto del artículo (no modificar)
// - highlight: sub-frase EXACTA dentro de `title` que se pinta en naranja en el <h1>
// - excerpt: meta descripción / bajada de tarjeta (según documento fuente)
// - keywords: intención SEO del documento fuente
// - date: fecha de publicación
// - readTime: tiempo estimado de lectura
// - content: cuerpo exacto del artículo (no modificar), párrafos separados por línea en blanco.
//            Los conceptos que deben verse en negrita sutil están envueltos en **doble asterisco**
//            (es solo una marca de formato, el texto entre asteriscos es idéntico al original).

export const BLOG_POSTS = [
  {
    number: 1,
    slug: "responsabilidad-sin-microgestion",
    image: "/blog-images/01.jpeg",
    category: "Accountability",
    title: "Responsabilidad sin microgestión: cómo lograr que cada líder se haga cargo",
    highlight: "microgestión",
    excerpt:
      "Cómo lograr que cada líder asuma sus compromisos sin perseguir tareas ni convertir al dueño en un supervisor permanente.",
    keywords: "responsabilidad empresarial, accountability sin microgestión, líderes que se hagan cargo",
    date: "06 de mayo, 2026",
    readTime: "2 min de lectura",
    content: `Si cada compromiso requiere que tú lo recuerdes, lo persigas y lo revises, tu equipo no está asumiendo **verdadera responsabilidad**. Está esperando que el jefe mantenga todo en movimiento. Ese modelo puede funcionar con pocas personas, pero se vuelve insostenible cuando la empresa crece.

La microgestión suele ser una respuesta a la **falta de claridad**. Cuando no está definido quién responde por cada función, qué resultado debe entregar y cuándo debe hacerlo, el seguimiento se transforma en conversaciones, recordatorios y excusas.

El problema no se resuelve controlando más. Se resuelve instalando una forma visible y constante de trabajar: **responsables únicos**, prioridades claras, números semanales y compromisos que se revisan con la misma disciplina todas las semanas.

EOS ayuda concretamente a definir un **Diagrama de Rendición de Cuentas** (mapa claro de funciones y responsables), **Rocas** (objetivos trimestrales), un **Tablero de Indicadores** (números semanales) y una **Reunión Nivel 10** (reunión semanal estructurada). Así, la responsabilidad deja de depender de perseguir personas y pasa a ser parte del sistema.

Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
  },
  {
    number: 2,
    slug: "persona-correcta-puesto-correcto",
    image: "/blog-images/02.jpeg",
    category: "Personas",
    title: "¿Tienes a la persona correcta en el puesto correcto?",
    highlight: "puesto correcto",
    excerpt:
      "Cómo distinguir entre una persona que no calza con la cultura y una buena persona que está ocupando el puesto equivocado.",
    keywords: "persona correcta puesto correcto, evaluar equipo de liderazgo, persona equivocada en el cargo",
    date: "03 de junio, 2026",
    readTime: "2 min de lectura",
    content: `Una persona puede ser muy querida, comprometida y valiosa para la cultura, pero **no lograr los resultados** que exige su cargo. También puede ocurrir lo contrario: alguien cumple sus números, pero genera fricción, desconfianza o comportamientos que dañan al equipo.

Cuando no existen **criterios comunes**, estas decisiones se vuelven personales. Se posterga una conversación difícil, se cambia el cargo varias veces o se espera que el problema se resuelva solo. Mientras tanto, el resto del equipo absorbe el costo.

La evaluación debe separar dos preguntas: ¿esta persona representa los valores de la empresa? y ¿**entiende el puesto, lo quiere y tiene la capacidad** para desempeñarlo? Tener claridad en ambas evita confundir lealtad, desempeño y ajuste cultural.

EOS ayuda concretamente con el **Analizador de Personas** (evaluación frente a los valores de la empresa) y la **Diagrama de Rendición de Cuentas** (estructura de puestos y funciones). Esto permite trabajar para tener **Personas Correctas en Puestos Correctos**, con un lenguaje común y decisiones menos subjetivas.

Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
  },
  {
    number: 3,
    slug: "equipo-liderazgo-conversaciones-dificiles",
    image: "/blog-images/03.jpeg",
    category: "Equipo de Liderazgo",
    title: "Cuando el equipo de liderazgo evita las conversaciones difíciles",
    highlight: "conversaciones difíciles",
    excerpt:
      "Por qué los problemas se acumulan cuando el equipo de liderazgo no habla con apertura y cómo crear una dinámica más sana y directa.",
    keywords: "equipo directivo evita conversaciones difíciles, conflictos equipo de liderazgo, falta de honestidad en reuniones",
    date: "10 de junio, 2026",
    readTime: "2 min de lectura",
    content: `Un equipo puede parecer cordial y, al mismo tiempo, estar **profundamente desconectado**. Los temas importantes se comentan fuera de la reunión, las diferencias se suavizan para no incomodar y los problemas de personas se mantienen abiertos durante meses.

Evitar el conflicto no crea un **equipo saludable**. Crea política, conversaciones paralelas y decisiones que nadie respalda realmente. La organización lo percibe rápido: si los líderes no pueden decirse la verdad, el resto de la empresa aprende a hacer lo mismo.

Un equipo fuerte necesita ser **abierto, honesto y vulnerable**. Esto no significa discutir por todo. Significa poner los asuntos sobre la mesa, escuchar perspectivas distintas y salir de la sala con una decisión y una sola voz frente a la organización.

EOS ayuda concretamente a instalar una dinámica de liderazgo más saludable mediante reuniones estructuradas, una **Lista de Asuntos** (problemas y oportunidades visibles) y el **método IDS** (Identificar, Discutir y Solucionar). El objetivo es hablar de lo que realmente importa y resolverlo para el bien de la empresa.

Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
  },
  {
    number: 4,
    slug: "roles-responsabilidades-confusas-empresa",
    image: "/blog-images/04.jpeg",
    category: "Estructura",
    title: "Roles confusos: cuando todos participan, pero nadie responde",
    highlight: "Roles confusos",
    excerpt:
      "Cómo la ambigüedad de roles genera duplicidad, vacíos y falta de responsabilidad en el equipo de liderazgo.",
    keywords: "roles y responsabilidades confusas, nadie se hace responsable, duplicidad de funciones empresa",
    date: "17 de junio, 2026",
    readTime: "2 min de lectura",
    content: `Cuando dos líderes creen que el otro tomará la decisión, **el problema queda sin dueño**. Cuando ambos creen que mandan, aparecen choques, duplicidad y mensajes contradictorios. En ambos casos, la organización pierde tiempo y velocidad.

Los organigramas tradicionales suelen mostrar jerarquía, pero no siempre explican qué **cinco o seis resultados esenciales** corresponden a cada puesto. Por eso, las personas terminan negociando responsabilidades caso a caso o escalando los desacuerdos al dueño.

La claridad exige diseñar primero la estructura que la empresa necesita y después decidir quién ocupará cada puesto. Cada función principal debe tener **un único responsable**, porque cuando dos personas responden por lo mismo, normalmente nadie responde de verdad.

EOS ayuda concretamente a construir una **Diagrama de Rendición de Cuentas** (estructura de funciones, puestos y responsables) mirando hacia los próximos seis a doce meses. Así se reducen la complejidad, los vacíos y las superposiciones, y cada líder sabe con claridad por qué resultados debe responder.

Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
  },
  {
    number: 5,
    slug: "empresa-dependiente-del-fundador",
    image: "/blog-images/05.jpeg",
    category: "Liderazgo",
    title: "Empresa dependiente del fundador: cómo dejar de ser el cuello de botella",
    highlight: "cuello de botella",
    excerpt:
      "Señales de que la empresa depende demasiado del fundador y qué debe cambiar para recuperar control sin centralizarlo todo.",
    keywords: "empresa dependiente del fundador, fundador cuello de botella, empresa funciona sin el dueño",
    date: "24 de junio, 2026",
    readTime: "2 min de lectura",
    content: `Si cada decisión importante termina en tu escritorio, **no tienes control: tienes dependencia**. La empresa puede crecer en ventas y personas, pero sigue funcionando alrededor de tu disponibilidad, tu memoria y tu capacidad para apagar incendios.

El fundador se convierte en **cuello de botella** cuando concentra decisiones, relaciones, conocimiento y validaciones. El equipo aprende a esperar, y el dueño termina trabajando más horas justo cuando la empresa debería comenzar a darle mayor libertad.

Salir de esa dinámica no significa desaparecer ni soltar la empresa a ciegas. Significa construir líderes que sepan qué deben lograr, entregarles **autoridad real** y revisar el negocio mediante prioridades, números y reuniones previsibles.

EOS ayuda concretamente a ordenar la estructura con una **Carta de Responsabilidades**, establecer **Rocas** (objetivos trimestrales), medir semanalmente con un **Tablero de Indicadores** y resolver asuntos en una **Reunión Nivel 10**. El propósito es que el negocio gane control sin depender permanentemente de ti.

Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
  },
  {
    number: 6,
    slug: "problemas-escalan-al-dueno",
    image: "/blog-images/06.jpeg",
    category: "Accountability",
    title: "Cuando todos los problemas terminan escalando al dueño",
    highlight: "escalando al dueño",
    excerpt:
      "Por qué los líderes escalan cada decisión al dueño y cómo devolver la resolución de problemas al nivel correcto.",
    keywords: "todos los problemas escalan al dueño, líderes no toman decisiones, equipo depende del gerente general",
    date: "08 de julio, 2026",
    readTime: "2 min de lectura",
    content: `Tienes gerentes, jefaturas y responsables, pero los problemas importantes **siguen llegando a ti**. El equipo informa, pide aprobación y espera instrucciones. Tú terminas resolviendo asuntos que deberían cerrarse varios niveles antes.

Esto ocurre cuando las personas no tienen claridad sobre su autoridad, temen equivocarse o saben que la decisión final siempre será revisada por el dueño. Con el tiempo, la empresa **entrena a sus líderes para escalar, no para resolver**.

El cambio exige definir qué decisiones corresponden a cada puesto, qué números deben cuidar y cómo deben abordar los problemas. También exige que el dueño **deje de recuperar decisiones que ya delegó** cada vez que no le gusta la primera respuesta.

EOS ayuda concretamente a aclarar la autoridad mediante la **Carta de Responsabilidades** y a crear una disciplina común para resolver problemas con **IDS** (Identificar, Discutir y Solucionar). Así, los asuntos se resuelven donde corresponde y el equipo de liderazgo gana capacidad real.

Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
  },
  {
    number: 7,
    slug: "dejar-de-apagar-incendios-empresa",
    image: "/blog-images/07.jpeg",
    category: "Visión y Prioridades",
    title: "Cómo dejar de apagar incendios y volver a dirigir la empresa",
    highlight: "apagar incendios",
    excerpt:
      "Cómo salir de la urgencia permanente y recuperar tiempo para dirigir, anticipar y trabajar en el futuro de la empresa.",
    keywords: "dejar de apagar incendios empresa, dueño atrapado en la operación, trabajar en el negocio y no solo dentro",
    date: "15 de julio, 2026",
    readTime: "2 min de lectura",
    content: `Hay días en que trabajas sin parar y, aun así, sientes que no avanzaste. Respondiste urgencias, destrabaste decisiones y resolviste problemas de clientes o personas, pero no dedicaste tiempo a las **decisiones que realmente cambian el futuro del negocio**.

La **urgencia permanente** suele ocultar tres debilidades: prioridades poco claras, problemas que nunca se resuelven de raíz y líderes que esperan que alguien más tome el control. Sin un sistema, cada semana comienza desde cero.

Dirigir exige **elevarse por encima de la operación**, mirar el negocio completo y decidir qué debe cambiar. Para lograrlo, el equipo necesita un ritmo que separe seguimiento, resolución de problemas y prioridades de mediano plazo.

EOS ayuda concretamente a crear un **Mundo de 90 Días** (ciclos de ejecución trimestral), una **Reunión Nivel 10** semanal y un método para resolver asuntos de raíz. Esto permite que el equipo atienda la operación sin perder de vista las prioridades que hacen avanzar la empresa.

Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
  },
  {
    number: 8,
    slug: "reuniones-largas-sin-resultados",
    image: "/blog-images/08.jpeg",
    category: "Reuniones",
    title: "Reuniones largas, decisiones débiles y compromisos que no se cumplen",
    highlight: "decisiones débiles",
    excerpt:
      "Cómo transformar reuniones extensas e informativas en un espacio semanal de seguimiento, decisiones y resolución real de problemas.",
    keywords: "reuniones largas sin resultados, reuniones gerenciales improductivas, compromisos no se cumplen",
    date: "22 de julio, 2026",
    readTime: "2 min de lectura",
    content: `Una reunión puede durar dos horas y no resolver nada. Se comparten novedades, se repiten antecedentes y aparecen temas nuevos, pero el equipo sale **sin decisiones claras**, responsables definidos ni fechas comprometidas.

El problema no es la duración. Es la falta de una **agenda constante** y de una disciplina para separar información, seguimiento y resolución. Cuando todo se mezcla, los asuntos importantes compiten con actualizaciones que podrían haberse enviado por correo.

Una buena reunión de liderazgo debe mostrar rápidamente si los números y prioridades están en curso, revisar compromisos de la semana anterior y dedicar la mayor parte del tiempo a **resolver los problemas más importantes**.

EOS ayuda concretamente con la **Reunión Nivel 10** (reunión semanal de 90 minutos con agenda fija). El equipo revisa el **Tablero de Indicadores**, las **Rocas** (objetivos trimestrales), los pendientes y luego utiliza **IDS** para resolver asuntos. La reunión deja de ser informativa y se vuelve una herramienta de ejecución.

Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
  },
  {
    number: 9,
    slug: "ventas-crecen-utilidad-baja",
    image: "/blog-images/09.jpeg",
    category: "Datos y Rentabilidad",
    title: "La empresa vende más, pero gana menos: dónde se está perdiendo la utilidad?",
    highlight: "perdiendo la utilidad",
    excerpt:
      "Cómo identificar las fugas operativas y de gestión que hacen crecer las ventas sin mejorar la utilidad.",
    keywords: "ventas crecen utilidad baja, empresa vende más pero gana menos, pérdida de rentabilidad operativa",
    date: "29 de julio, 2026",
    readTime: "2 min de lectura",
    content: `Crecer en ventas **no garantiza crecer en utilidad**. El volumen puede aumentar al mismo tiempo que suben los retrabajos, descuentos, horas extra, incumplimientos, costos de coordinación y decisiones comerciales que nadie mide con suficiente frecuencia.

Mirar solamente el estado de resultados explica lo que ya ocurrió. Para cambiar el resultado necesitas identificar qué comportamientos y números semanales anticipan la **pérdida de margen**: productividad, ocupación, descuentos, errores, tiempos de entrega o cobranza, según tu negocio.

También necesitas saber quién responde por cada número y qué problema debe abrirse cuando una meta se desvía. Sin esa conexión, el equipo **explica la rentabilidad tarde**, pero no la administra mientras todavía puede influir en ella.

EOS no reemplaza la estrategia financiera ni el conocimiento técnico de tu industria. Ayuda concretamente a instalar un **Tablero de Indicadores** semanal, responsables claros y una disciplina para resolver las causas que deterioran el resultado. Así, la utilidad se conversa y gestiona antes del cierre mensual.

Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
  },
  {
    number: 10,
    slug: "indicadores-semanales-negocio",
    image: "/blog-images/10.jpeg",
    category: "Datos",
    title: "Indicadores semanales: cómo saber antes si el negocio está fuera de rumbo",
    highlight: "fuera de rumbo",
    excerpt:
      "Cómo utilizar pocos números semanales para anticipar problemas, ver patrones y actuar antes de que cierre el mes.",
    keywords: "indicadores semanales empresa, tablero semanal de gestión, anticipar problemas del negocio",
    date: "05 de agosto, 2026",
    readTime: "2 min de lectura",
    content: `Esperar el cierre mensual para saber cómo va la empresa es **conducir mirando el espejo retrovisor**. Cuando aparece el resultado, muchas de las decisiones que lo provocaron ocurrieron semanas antes y ya no pueden corregirse a tiempo.

El problema tampoco se resuelve acumulando cientos de indicadores. Un equipo de liderazgo necesita **pocos números, realmente relevantes**, que muestren si ventas, operación, caja, clientes y personas están avanzando dentro de lo esperado.

Cada número debe tener una **meta semanal y un responsable**. La historia de varias semanas permite reconocer tendencias, no solo fotografías aisladas, y convertir cualquier desviación en un asunto que el equipo pueda resolver.

EOS ayuda concretamente con un **Tablero de Indicadores** de 5 a 15 números semanales, cada uno con responsable, meta e historia de 13 semanas. Esto reemplaza opiniones y sensaciones por un pulso objetivo que permite predecir, detectar patrones y actuar antes de que sea tarde.

Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
  },
  {
    number: 11,
    slug: "inteligencia-artificial-y-eos",
    image: "/blog-images/11.jpeg",
    category: "Inteligencia Artificial",
    title: "La IA te ayuda a hacer más. EOS te ayuda a decidir qué vale la pena hacer.",
    highlight: "decidir qué vale la pena hacer",
    excerpt:
      "Por qué la pregunta correcta no es cuánta IA incorporar, sino dónde genera impacto real — y cómo EOS ayuda a decidirlo con Rocas y Scorecard.",
    keywords: "inteligencia artificial en empresas, IA y EOS, priorizar iniciativas de IA, IA con foco estratégico",
    date: "25 de agosto, 2026",
    readTime: "3 min de lectura",
    // Firma/CTA (se renderiza al final de los bloques, igual que en los artículos en prosa)
    content: `Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
    // Formato "guía visual": bloques enriquecidos (ver BlogBlocks.jsx)
    blocks: [
      {
        type: "paragraph",
        text: "La inteligencia artificial promete acelerar todo, pero acelerar sin dirección solo multiplica el ruido. Antes de sumar una herramienta más, un equipo de liderazgo necesita responder algo más simple: ¿qué resultado del negocio queremos mover? Esta es una guía práctica en 5 pasos para conectar la IA con la estrategia, usando el mismo marco que EOS aplica a cualquier prioridad: foco, responsable y un número que lo demuestre.",
      },
      {
        type: "step",
        number: 1,
        name: "Resultado",
        title: "Parte del resultado, no de la herramienta.",
        body: "No empieces por la herramienta. Empieza por el objetivo del negocio que necesitas mover.\n\nUsa el **V/TO** de EOS para identificar qué prioridad debe avanzar antes de evaluar qué IA usar.\n\nAsí la tecnología queda conectada con la estrategia desde el primer día, en lugar de sumarse como un experimento aislado.",
      },
      {
        type: "step",
        number: 2,
        name: "Proceso",
        title: "Elige un proceso relevante.",
        body: "Busca una fricción concreta: demoras, reprocesos o tareas repetitivas que hoy limitan al negocio.\n\nLa IA debe resolver un problema real que hoy limita al negocio, no llenar un vacío que nadie pidió llenar.",
      },
      { type: "quote", text: "Primero claridad. Después, aceleración." },
      {
        type: "step",
        number: 3,
        name: "Medición",
        title: "Define una mejora medible.",
        body: "Lo que no se mide termina siendo solo una percepción. Antes de avanzar, responde tres preguntas:",
      },
      { type: "chips", items: ["¿Cuál es el punto de partida?", "¿Qué cambio esperamos?", "¿Qué número lo demostrará?"] },
      {
        type: "step",
        number: 4,
        name: "Roca de 90 días",
        title: "Convierte la iniciativa en una Roca de 90 días.",
        body: "La iniciativa ya tiene dueño, foco y plazo. EOS convierte la adopción de IA en ejecución.",
      },
      { type: "chips", items: ["Resultado concreto", "Una persona accountable", "Un indicador semanal de pulso", "Fecha límite clara"] },
      {
        type: "step",
        number: 5,
        name: "Ejecución semanal",
        title: "Mide y resuelve, cada semana.",
        body: "Revisa cada semana si la mejora está ocurriendo.\n\nSi aparece un obstáculo, llévalo a **IDS** (Identifica, Discute, Soluciona).\n\nLa IA acelera; el equipo de liderazgo toma las decisiones.",
      },
      {
        type: "paragraph",
        text: "**Así se ve en la práctica.** Un caso pequeño, concreto y medible:",
      },
      {
        type: "stat",
        value: "5 → 1 día",
        label: "Tiempo para preparar una propuesta comercial",
        subtext: "Usando IA para resumir y ordenar información antes de redactar",
      },
      { type: "chips", items: ["Proceso: preparar una propuesta", "Uso de IA: resumir y ordenar", "Seguimiento: Scorecard semanal"] },
      {
        type: "punchline",
        lines: ["La IA te ayuda a hacer más.", "EOS te ayuda a decidir qué vale la pena hacer."],
      },
      {
        type: "paragraph",
        text: "EOS conecta la IA con la estrategia de la empresa y su ejecución. ¿Qué proceso elegirías para empezar?",
      },
    ],
  },
  {
    number: 12,
    slug: "como-construir-un-tablero-de-indicadores",
    image: "/blog-images/12.jpeg",
    category: "Datos",
    title: "¿Cómo construir un tablero de indicadores que realmente sirve?",
    highlight: "que realmente sirve",
    excerpt:
      "Guía práctica en 5 pasos para construir un Tablero de Indicadores (Scorecard) que detecte problemas antes de que aparezcan en los resultados del mes.",
    keywords: "tablero de indicadores empresa, scorecard EOS, cómo armar un scorecard, indicadores semanales liderazgo, KPIs semanales EOS",
    date: "01 de septiembre, 2026",
    readTime: "3 min de lectura",
    // Firma/CTA (se renderiza al final de los bloques, igual que en los artículos en prosa)
    content: `Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
    // Formato "guía visual": bloques enriquecidos (ver BlogBlocks.jsx)
    blocks: [
      {
        type: "paragraph",
        text: "La mayoría de los tableros de indicadores fracasan por la misma razón: intentan explicar toda la empresa. El resultado es un panel lleno de números que nadie revisa con disciplina, porque no ayuda a decidir nada en concreto. Un buen Tablero de Indicadores no mide todo — mide las pocas señales que tu equipo de liderazgo necesita para actuar a tiempo.",
      },
      {
        type: "step",
        number: 1,
        name: "Simplifica",
        title: "Mide menos. Entiende más.",
        body: "Tu Tablero de Indicadores no debe explicar toda la empresa. Debe mostrar las pocas señales que necesitas para actuar a tiempo.\n\nEl cuadro debe ayudarte a responder tres preguntas: **¿vendimos lo suficiente?, ¿lo hicimos con el margen correcto?, ¿cumplimos a tiempo?**\n\nSi un número no ayuda a decidir, probablemente no debería estar ahí.",
      },
      { type: "stat", value: "5 a 15", label: "números semanales", subtext: "para el equipo de liderazgo" },
      {
        type: "step",
        number: 2,
        name: "Liderazgo",
        title: "Empieza por el equipo de liderazgo",
        body: "Conecta los indicadores con los objetivos del V/TO. Cada función clave debe aportar un número que muestre si la empresa avanza hacia su Visión.",
      },
      { type: "chips", items: ["Comercial", "Operaciones", "Finanzas", "Personas"] },
      {
        type: "paragraph",
        text: "**Un número por función. Una persona responsable. Una meta clara.**",
      },
      {
        type: "step",
        number: 3,
        name: "Buena semana",
        title: "Define qué significa una buena semana",
        body: "Convierte la respuesta en una actividad o resultado medible, revisable cada siete días.",
      },
      { type: "quote", text: "¿Cómo sabes que tuviste una buena semana?" },
      { type: "chips", items: ["Unidades entregadas", "Llamadas realizadas", "Propuestas enviadas", "Entregas a tiempo"] },
      {
        type: "paragraph",
        text: "Si no tiene frecuencia semanal, meta y responsable, aún no está listo para el tablero.",
      },
      {
        type: "step",
        number: 4,
        name: "Procesos",
        title: "Mide tus procesos clave",
        body: "Elige indicadores que te digan si tus procesos operaron bien durante la semana anterior.\n\nNo midas el proceso completo: mide la señal semanal que confirma si está funcionando. Esa señal correcta permite detectar el problema antes de verlo en el resultado mensual.",
      },
      { type: "chips", items: ["Marketing", "Ventas", "Operaciones", "Finanzas", "Personas"] },
      {
        type: "step",
        number: 5,
        name: "Áreas",
        title: "Lleva el Tablero de Indicadores a cada área",
        body: "Repite los pasos por departamento. Cada nivel debe explicar qué impulsa el número que está arriba.",
      },
      {
        type: "cascade",
        levels: [
          { label: "Empresa", desc: "Número clave del equipo de liderazgo" },
          { label: "Área", desc: "Causas que impulsan ese número" },
          { label: "Persona", desc: "Actividad semanal bajo su responsabilidad" },
        ],
      },
      {
        type: "paragraph",
        text: "Cada persona debe ser responsable de al menos un medible que contribuya al objetivo de su área.",
      },
      {
        type: "punchline",
        lines: [
          "Un buen Tablero de Indicadores no mide todo.",
          "Menos ruido. Más claridad. Mejores decisiones.",
        ],
      },
    ],
  },
  {
    number: 13,
    slug: "herramientas-tecnologicas-para-gestionar-eos-ninety",
    image: "/blog-images/13.jpeg",
    category: "Herramientas",
    title: "Herramientas tecnológicas para gestionar EOS: Ninety",
    highlight: "Ninety",
    excerpt:
      "EOS es agnóstico de la herramienta: se puede llevar en papel o planillas. Con Ninety.io reúno todas las herramientas de EOS en un solo lugar, con IA Maz.",
    keywords: "ninety.io, software para EOS, herramientas tecnológicas EOS, plataforma para gestionar EOS, maz inteligencia artificial, scorecard EOS online",
    date: "07 de septiembre, 2026",
    readTime: "3 min de lectura",
    // Firma/CTA (se renderiza al final de los bloques, igual que en los artículos en prosa)
    content: `Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
    // Formato "guía visual": bloques enriquecidos (ver BlogBlocks.jsx)
    blocks: [
      {
        type: "paragraph",
        text: "**EOS es agnóstico respecto de la herramienta que uses para gestionarlo. Se puede llevar perfectamente en papel, en planillas, en documentos compartidos o en alguna plataforma online.** Lo importante es respetar la metodología y mantener la disciplina. Dicho eso, mi experiencia trabajando con **Ninety.io** ha sido positiva, principalmente porque permite ordenar las herramientas de EOS en un solo lugar y hacer mucho más simple la **gestión semanal** del equipo.",
      },
      {
        type: "paragraph",
        text: "Ninety reúne buena parte de lo que un equipo necesita para operar EOS:",
      },
      { type: "chips", items: ["V/TO", "Accountability Chart", "Rocas", "Scorecard", "Issues", "To-Dos", "Level 10 Meetings"] },
      {
        type: "paragraph",
        text: "La ventaja está en que todo queda conectado. Las prioridades trimestrales, los indicadores, los asuntos y los compromisos no viven en archivos distintos, sino dentro de una misma plataforma que el equipo puede revisar y actualizar semana a semana.",
      },
      {
        type: "paragraph",
        text: "Esto se nota especialmente en las reuniones. Una roca que está off track, un indicador del Scorecard fuera de objetivo o un tema que aparece durante la semana puede llevarse rápidamente a la lista de Issues para resolverlo mediante **IDS**. De la misma forma, una decisión puede transformarse inmediatamente en un To-Do con un responsable.",
      },
      { type: "quote", text: "Menos tiempo administrando información y más tiempo resolviendo asuntos y ejecutando." },
      {
        type: "paragraph",
        text: "Ninety también está incorporando **inteligencia artificial** a través de **Maz**, su asistente de IA. Maz trabaja con la información que ya existe dentro de la plataforma y permite consultar datos de la empresa, revisar rocas, Scorecards, Issues, reuniones o el Accountability Chart.",
      },
      { type: "chips", items: ["Consultar datos de la empresa", "Detectar tendencias", "Encontrar información más rápido", "Apoyar la creación o revisión de elementos"] },
      {
        type: "paragraph",
        text: "Bien utilizada, la IA se convierte en una ayuda práctica para el equipo, especialmente cuando empieza a acumularse más información.\n\nPara quienes están implementando EOS, Ninety ayuda a que la gestión sea mucho más práctica. Mantiene las herramientas ordenadas, facilita las reuniones, deja claros los responsables y permite seguir la ejecución sin depender de múltiples planillas o documentos.",
      },
      {
        type: "punchline",
        lines: [
          "Se trata de hacer más fácil algo que en EOS es fundamental:",
          "Mantener foco, visibilidad y disciplina semana tras semana.",
        ],
      },
      {
        type: "paragraph",
        text: "Si estás implementando EOS y quieres probar Ninety, en este enlace [te darán 30 días gratuitos de uso de la herramienta completa: Probar Ninety.](https://hltx.partnerlinks.io/6a26f7d486acc6812466e79b)",
      },
    ],
  },
  {
    number: 14,
    slug: "organizacion-correcta-antes-de-completar-el-equipo",
    image: "/blog-images/14.jpeg",
    category: "Estructura",
    title: "La organización correcta se construye antes de completar el equipo",
    highlight: "antes de completar el equipo",
    excerpt:
      "Al crecer, es común descubrir funciones superpuestas y responsabilidades sin dueño. EOS enseña a diseñar primero la estructura y luego completarla.",
    keywords: "accountability chart eos, estructura organizacional empresa, organigrama eos, personas correctas roles correctos, gwc get it want it capacity, asientos vacíos organigrama",
    date: "15 de septiembre, 2026",
    readTime: "4 min de lectura",
    // Firma/CTA (se renderiza al final de los bloques, igual que en los artículos en prosa)
    content: `Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
    // Formato "guía visual": bloques enriquecidos (ver BlogBlocks.jsx)
    blocks: [
      {
        type: "paragraph",
        text: "**Al crecer, es habitual descubrir funciones que se superponen, responsabilidades sin dueño y capacidades que aún faltan.** Esperar a resolver todo eso antes de ordenar la empresa invierte la secuencia: contratar personas sin haber definido con claridad el trabajo que necesitamos que hagan.",
      },
      {
        type: "step",
        number: 1,
        name: "Estructura",
        title: "Primero, diseñar la estructura",
        body: "El Accountability Chart de EOS parte de una pregunta distinta a la de un organigrama tradicional: ¿qué funciones y responsabilidades necesita este negocio para avanzar? Se dibujan los asientos y lo que cada uno debe producir, antes de poner nombres.\n\nLa guía oficial de EOS lo dice sin rodeos: algunos asientos quedarán vacíos.",
      },
      { type: "quote", text: "Un asiento vacío no es una falla del ejercicio; es una necesidad que ahora podemos ver y gestionar." },
      {
        type: "step",
        number: 2,
        name: "Personas",
        title: "Después, evaluar y completar los asientos",
        body: "Con el trabajo definido, la conversación sobre personas se vuelve más rigurosa. EOS distingue a la **persona correcta** —quien vive los valores de la empresa— de la **persona en el asiento correcto**, quien entiende el rol, lo quiere y tiene capacidad para desempeñarlo.\n\nEl sistema ayuda a evaluar al equipo actual, reconocer brechas y contratar con criterios más claros, sin exigir que todas esas decisiones estén resueltas de antemano.",
      },
      { type: "chips", items: ["Entiende (Get it)", "Quiere (Want it)", "Capacidad de hacerlo (Capacity)"] },
      {
        type: "paragraph",
        text: "Los líderes suelen heredar equipos que deben evaluar, reconfigurar y desarrollar mientras operan. Una dirección compartida y una estructura sólida también son condiciones para que un equipo funcione bien; no todo depende de reunir previamente a las personas ideales.",
      },
      {
        type: "paragraph",
        text: "**Comenzar no significa ignorar los vacíos.** Una función crítica sin líder sigue siendo un riesgo. Mientras se busca a quien la ocupe, sus responsabilidades indispensables necesitan un dueño interino, resultados visibles y seguimiento.\n\nTampoco basta con tener asientos dibujados: debe existir un equipo de liderazgo dispuesto a decidir, asumir responsabilidades y sostener la disciplina de ejecución. La estructura puede revisarse cuando se incorporen nuevas personas; no queda congelada por haber empezado.",
      },
      {
        type: "punchline",
        lines: [
          "No hace falta una foto perfecta del equipo para comenzar.",
          "Hace falta construir una organización clara y tomar mejores decisiones sobre las personas.",
        ],
      },
      {
        type: "links",
        heading: "Para profundizar",
        items: [
          { label: "EOS Worldwide — How to Get the Right People in the Right Seats", url: "https://www.eosworldwide.com/blog/how-to-get-the-right-people-in-the-right-seats-and-keep-them-there" },
          { label: "HBR — Leading the Team You Inherit", url: "https://hbr.org/2016/06/leading-the-team-you-inherit" },
          { label: "HBR — The Secrets of Great Teamwork", url: "https://hbr.org/2016/06/the-secrets-of-great-teamwork" },
        ],
      },
    ],
  },
  {
    number: 15,
    slug: "crecer-sin-que-tu-empresa-se-vuelva-mas-compleja",
    image: "/blog-images/15.jpg",
    category: "Liderazgo",
    title: "Cómo crecer sin que tu empresa se vuelva más compleja",
    highlight: "sin que tu empresa se vuelva más compleja",
    excerpt:
      "Crecer sin desordenar tu empresa exige claridad de responsabilidades, procesos simples y pocas prioridades. Así ayuda EOS a escalar sin más complejidad.",
    keywords: "crecer sin complejidad empresa, escalar empresa sin desorden, sistema de gestión EOS, procesos simples crecimiento, decisiones dueño empresa",
    date: "28 de septiembre, 2026",
    readTime: "3 min de lectura",
    content: `Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
    blocks: [
      {
        type: "paragraph",
        text: "Crecer es una buena noticia, pero también pone a prueba la forma en que funciona una empresa. Lo que servía cuando eran 10 personas puede empezar a fallar cuando son 30, 50 o 100. Aparecen más clientes, más decisiones, más personas involucradas y, si la forma de trabajar no evoluciona al mismo ritmo, el crecimiento empieza a traer desorden.",
      },
      { type: "quote", text: "El problema no es crecer: es intentar administrar una empresa más grande con la misma estructura que tenía cuando era pequeña." },
      {
        type: "paragraph",
        text: "Una de las señales más comunes es que **demasiadas decisiones siguen pasando por el dueño o por un pequeño grupo de personas**. También aparecen responsabilidades poco claras, tareas que nadie sabe exactamente quién debe resolver y líderes que terminan revisando constantemente el trabajo de otros. Contratar más personas no necesariamente soluciona esto. Para escalar una empresa, primero tiene que existir **claridad sobre quién es responsable de cada resultado y qué decisiones puede tomar cada persona**.",
      },
      { type: "chips", items: ["Decisiones concentradas en el dueño", "Responsabilidades poco claras", "Revisión constante del trabajo"] },
      {
        type: "paragraph",
        text: "El crecimiento también hace visibles ineficiencias que antes podían pasar desapercibidas. Retrabajos, errores, procesos que cada persona ejecuta de una manera diferente o problemas que vuelven una y otra vez empiezan a consumir tiempo y rentabilidad. Cuando la operación depende principalmente de lo que las personas recuerdan o de cómo “siempre se ha hecho”, aumentar el volumen termina aumentando también los problemas.",
      },
      { type: "quote", text: "Tener procesos simples y una forma común de trabajar permite crecer con mayor consistencia sin convertir la organización en una burocracia." },
      {
        type: "paragraph",
        text: "Por eso, crecer de manera sostenible no significa agregar más controles ni llenar la empresa de procedimientos. Significa construir un sistema de gestión que entregue claridad: una visión compartida, responsabilidades claras, pocos indicadores relevantes, procesos simples y prioridades concretas para los próximos 90 días. EOS ayuda precisamente a ordenar esas piezas.",
      },
      { type: "chips", items: ["Visión compartida", "Responsabilidades claras", "Pocos indicadores relevantes", "Procesos simples", "Prioridades a 90 días"] },
      {
        type: "punchline",
        lines: [
          "La meta es que el crecimiento no dependa de trabajar más horas o de que el dueño esté en todas partes,",
          "sino de construir una empresa capaz de funcionar cada vez mejor a medida que crece.",
        ],
      },
    ],
  },
  {
    number: 16,
    slug: "contrataste-un-gerente-que-esperas-de-el",
    image: "/blog-images/16.jpeg",
    category: "Accountability",
    title: "Contrataste un gerente, pero ¿le dijiste realmente qué esperas de él?",
    highlight: "¿le dijiste realmente qué esperas de él?",
    excerpt:
      "Contratar un gerente no basta si no está claro qué resultado debe lograr. Cómo la claridad en EOS permite delegar de verdad y reduce el control.",
    keywords: "contrataste un gerente, delegar sin microgestión, accountability gerentes, expectativas claras gerente, falsa delegación, EOS",
    date: "01 de octubre, 2026",
    readTime: "3 min de lectura",
    content: `Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
    blocks: [
      {
        type: "paragraph",
        text: "Contratar a un buen gerente debería permitirle al **dueño dejar de estar encima de cada decisión**. Sin embargo, muchas veces ocurre exactamente lo contrario: pasan los meses, los resultados no llegan y aparece la sensación de que “la persona no se hace cargo”.",
      },
      { type: "quote", text: "Antes de concluir que falta compromiso o capacidad, hay una pregunta bastante más incómoda que hacerse: ¿estaba realmente claro qué resultado debía conseguir esa persona?" },
      {
        type: "paragraph",
        text: "Decirle a alguien que es responsable de ventas, operaciones o finanzas **no necesariamente entrega esa claridad**. Una función puede estar perfectamente definida en el organigrama y, aun así, existir expectativas completamente distintas entre el dueño y el gerente. Uno cree que entregó autonomía; el otro intenta interpretar qué significa hacerlo bien. Y cuando los resultados no coinciden con lo esperado, la conversación empieza desde un lugar injusto: se evalúa a alguien contra una meta que quizás nunca fue acordada con suficiente precisión.",
      },
      {
        type: "paragraph",
        text: "A eso se suma otro problema habitual: **pedir responsabilidad sin entregar verdadera capacidad para decidir**. Si el gerente tiene que consultar cada contratación, descuento, inversión o cambio relevante, en la práctica sigue dependiendo del dueño para hacer su trabajo.",
      },
      { type: "image", src: "/blog-images/16-b.jpeg", alt: "Delegar no es decir “hazte cargo”: si cada decisión importante vuelve al dueño, es falsa delegación" },
      { type: "quote", text: "No basta con decir “confío en ti”." },
      {
        type: "paragraph",
        text: "Tiene que estar claro **qué resultado se espera, qué decisiones puede tomar y cómo ambos sabrán objetivamente si va por buen camino**. Esa combinación permite que una persona realmente se haga cargo.",
      },
      { type: "chips", items: ["Qué resultado se espera", "Qué decisiones puede tomar", "Cómo sabrán si va por buen camino"] },
      { type: "image", src: "/blog-images/16-c.jpeg", alt: "La accountability necesita tres cosas claras: qué resultado espero, qué puedes decidir y cómo sabremos si vamos bien" },
      {
        type: "paragraph",
        text: "La claridad también mejora la forma de gestionar el desempeño. Cuando existen **objetivos concretos, prioridades definidas y pocos indicadores** que muestran si las cosas avanzan o no, disminuyen las interpretaciones. Ya no se trata de si alguien “parece comprometido” o “podría estar haciendo más”. Se puede conversar sobre hechos. Eso beneficia tanto al dueño como al gerente: uno deja de perseguir permanentemente y el otro sabe exactamente qué significa cumplir bien con su responsabilidad.",
      },
      {
        type: "paragraph",
        text: "En EOS este problema se trabaja conectando varias piezas que tienen que funcionar juntas: **una estructura con responsabilidades claras, prioridades de corto plazo con un dueño definido y números que permitan ver semanalmente si el negocio va bien o mal**. El resultado práctico no es agregar control, sino justamente reducirlo: menos decisiones concentradas en el dueño, gerentes con mayor autonomía, conversaciones de desempeño más objetivas y una organización donde las personas saben qué tienen que lograr y pueden responder realmente por ello.",
      },
      { type: "image", src: "/blog-images/16-d.jpeg", alt: "La claridad no agrega control, lo reduce: menos decisiones concentradas en el dueño y más autonomía" },
    ],
  },
  {
      "number": 17,
      "slug": "persona-correcta-puesto-correcto-gwc-eos",
      "image": "/blog-images/17.jpeg",
      "category": "Personas",
      "title": "Cómo saber si una persona está en el puesto correcto",
      "highlight": "puesto correcto",
      "seoTitle": "Cómo saber si una persona está en el puesto correcto | GWC y EOS",
      "excerpt": "¿Tienes buenos colaboradores que no terminan de funcionar en su puesto? Conoce las 3 preguntas de GWC para saber si alguien está en el puesto correcto.",
      "keywords": "cómo saber si una persona está en el puesto correcto, GWC EOS, Get it Want it Capacity to do it, persona correcta puesto correcto, evaluar colaboradores, persona equivocada en el puesto",
      "date": "07 de octubre, 2026",
      "readTime": "3 min de lectura",
      "content": "Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica\n¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.",
      "blocks": [
          {
              "type": "paragraph",
              "text": "Puedes tener una **muy buena persona** en tu empresa y, aun así, tenerla en el **puesto equivocado**. Es más común de lo que parece: colaboradores comprometidos, con experiencia y años en la organización, pero donde el líder sigue teniendo que intervenir, revisar o terminar parte de su trabajo."
          },
          {
              "type": "quote",
              "text": "Antes de concluir que esa persona “no funciona”, vale la pena hacerse una pregunta más útil: ¿está realmente en el puesto correcto?"
          },
          {
              "type": "paragraph",
              "text": "En EOS utilizamos una herramienta muy simple para evaluarlo: **GWC®**, por Get it, Want it, Capacity to do it. En términos prácticos: ¿lo entiende?, ¿lo quiere?, ¿tiene la capacidad? Para considerar que alguien está realmente en el puesto correcto, **las tres respuestas deberían ser sí**."
          },
          {
              "type": "chips",
              "items": [
                  "¿Lo entiende?",
                  "¿Lo quiere?",
                  "¿Tiene la capacidad?"
              ]
          },
          {
              "type": "heading",
              "badge": "G",
              "text": "Get it: ¿lo entiende naturalmente?"
          },
          {
              "type": "paragraph",
              "text": "Hay personas que simplemente “lo captan”. Entienden naturalmente cómo funciona el puesto, qué se espera de ellas y qué tienen que hacer para que las cosas funcionen bien. Es esa **facilidad natural** para un determinado tipo de responsabilidad. No significa que una persona sea más o menos capaz en términos generales; puede ser excelente profesionalmente y, simplemente, no tener ese encaje natural con ese puesto en particular."
          },
          {
              "type": "image",
              "src": "/blog-images/17-b.jpeg",
              "alt": "G = Get it: ¿lo entiende naturalmente? Hay personas que simplemente “lo captan”"
          },
          {
              "type": "heading",
              "badge": "W",
              "text": "Want it: ¿realmente lo quiere?"
          },
          {
              "type": "paragraph",
              "text": "No basta con poder hacerlo. La persona tiene que **querer estar ahí**, asumir esa responsabilidad y levantarse con ganas de desempeñar bien ese rol. Este punto es especialmente importante porque muchas veces intentamos solucionar la falta de ganas con incentivos, conversaciones o presión. Pero **no puedes capacitar, pagar o convencer a alguien para que genuinamente quiera un puesto**. Incluso alguien que durante años quiso hacerlo puede llegar a un momento en que simplemente ya no lo quiera."
          },
          {
              "type": "image",
              "src": "/blog-images/17-c.jpeg",
              "alt": "W = Want it: ¿realmente lo quiere? No puedes convencer a alguien para que realmente lo quiera"
          },
          {
              "type": "heading",
              "badge": "C",
              "text": "Capacity: ¿tiene la capacidad para hacerlo bien?"
          },
          {
              "type": "paragraph",
              "text": "Aquí hablamos de las **habilidades y conocimientos adquiridos** que permiten hacer bien el trabajo: formación, experiencia, práctica y capacidad para responder a las exigencias del puesto. A diferencia de los dos puntos anteriores, **una brecha de capacidad puede, en algunos casos, desarrollarse** mediante aprendizaje, experiencia o capacitación. EOS también considera dimensiones como la capacidad intelectual, emocional, física y de tiempo."
          },
          {
              "type": "image",
              "src": "/blog-images/17-d.jpeg",
              "alt": "C = Capacity: ¿tiene la capacidad? Estudios, experiencia y práctica que se pueden desarrollar"
          },
          {
              "type": "quote",
              "text": "La regla es simple: lo entiende, lo quiere y tiene la capacidad."
          },
          {
              "type": "paragraph",
              "text": "Si una de las respuestas es no, **tienes un asunto que resolver**. Eso no significa necesariamente que tengas una mala persona en tu organización. Muchas veces tienes una **buena persona sentada en el puesto equivocado**. Y mientras eso no se resuelva, es muy probable que el líder siga compensando, supervisando en exceso o haciendo trabajo que debería haber delegado."
          },
          {
              "type": "image",
              "src": "/blog-images/17-e.jpeg",
              "alt": "La regla es simple: 3 sí. Lo entiende, lo quiere y tiene la capacidad"
          },
          {
              "type": "heading",
              "text": "Preguntas frecuentes"
          },
          {
              "type": "faq",
              "items": [
                  {
                      "q": "¿Cómo saber si un colaborador está en el puesto correcto?",
                      "a": "Pregúntate si entiende naturalmente el rol, si realmente quiere desempeñarlo y si tiene la capacidad necesaria para hacerlo bien. Si alguna respuesta es no, vale la pena revisar el puesto."
                  },
                  {
                      "q": "¿Qué pasa si tiene experiencia, pero ya no quiere el puesto?",
                      "a": "La experiencia no reemplaza las ganas de asumir esa responsabilidad. Puede entender el trabajo y ser capaz de hacerlo, pero si genuinamente ya no lo quiere, tienes un asunto que resolver."
                  },
                  {
                      "q": "¿Se puede capacitar a una persona para que funcione mejor en su puesto?",
                      "a": "Cuando el problema es de capacidad, en algunos casos sí. Una brecha de conocimientos o habilidades puede desarrollarse. Pero eso es distinto a intentar enseñarle a alguien a querer un puesto o a tener una facilidad natural que no posee."
                  }
              ]
          },
          {
              "type": "callout",
              "text": "Si este tipo de situaciones te resulta familiar y quieres revisar cómo está estructurado tu equipo, **conversemos**. Puedo ayudarte a mirar tu organización con herramientas prácticas como GWC y el Accountability Chart, identificar dónde pueden estar los principales asuntos y generar claridad antes de tomar decisiones."
          }
      ]
  },
  {
      "number": 18,
      "slug": "por-que-los-mismos-problemas-vuelven-en-tu-empresa",
      "image": "/blog-images/18.jpeg",
      "category": "Herramientas",
      "title": "¿Por qué los mismos problemas vuelven una y otra vez en tu empresa?",
      "highlight": "vuelven una y otra vez",
      "seoTitle": "Por qué los mismos problemas vuelven en tu empresa | EOS",
      "excerpt": "Atrasos, reclamos y decisiones que se repiten. Descubre por qué los mismos problemas vuelven y cómo IDS ayuda a resolverlos de raíz.",
      "keywords": "por qué los problemas vuelven en la empresa, problemas recurrentes, IDS EOS, causa raíz, resolver problemas en equipo, síntoma y causa, Identificar Discutir Resolver",
      "date": "20 de octubre, 2026",
      "readTime": "4 min de lectura",
      "content": "Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica\n¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.",
      "blocks": [
          {
              "type": "paragraph",
              "text": "Hay problemas que parecen tener vida propia: **atrasos que se repiten**, clientes que reclaman por lo mismo, decisiones que siempre terminan escalando o números que nunca mejoran del todo. El equipo conversa, acuerda una solución y sigue adelante. Pero unas semanas después, **el tema vuelve a aparecer**."
          },
          {
              "type": "quote",
              "text": "Cuando eso pasa, la pregunta no debería ser “¿cómo lo arreglamos otra vez?”, sino “¿alguna vez resolvimos realmente el problema?”."
          },
          {
              "type": "paragraph",
              "text": "EOS identifica tres causas habituales: **se atacó el síntoma y no la causa**, **la conversación se cerró demasiado pronto** o **nunca se tomó una decisión clara sobre qué debía cambiar**."
          },
          {
              "type": "chips",
              "items": [
                  "Se atacó el síntoma, no la causa",
                  "La conversación se cerró demasiado pronto",
                  "Nunca se tomó una decisión clara"
              ]
          },
          {
              "type": "heading",
              "text": "Confundir lo visible con lo importante"
          },
          {
              "type": "paragraph",
              "text": "El error más común es **confundir lo visible con lo importante**. Si un proyecto se atrasa porque una aprobación llegó tarde, la solución inmediata puede ser “recordar antes”. Pero si el verdadero problema es que **nadie sabe quién tiene la autoridad final para aprobar**, agregar recordatorios solo **administra el síntoma**. Lo mismo ocurre con ventas, operaciones o servicio al cliente: muchas organizaciones pasan meses corrigiendo consecuencias porque nunca se detienen a preguntar **qué tendría que cambiar para que este problema deje de ocurrir**."
          },
          {
              "type": "image",
              "src": "/blog-images/18-b.jpeg",
              "alt": "Resolver el síntoma da alivio, no necesariamente resuelve el problema: iceberg con la causa bajo la superficie"
          },
          {
              "type": "heading",
              "text": "Detrás del problema, una decisión pendiente"
          },
          {
              "type": "paragraph",
              "text": "También hay problemas que persisten porque en realidad **esconden una decisión pendiente**. El equipo puede entender perfectamente lo que ocurre y aun así discutirlo una y otra vez porque nadie quiere decidir si hay que contratar, cambiar un proceso, redefinir una responsabilidad o simplemente decir que algo deja de hacerse."
          },
          {
              "type": "quote",
              "text": "Conversar mucho no es lo mismo que resolver."
          },
          {
              "type": "paragraph",
              "text": "Una buena reunión debería **terminar con algo reconocible: una decisión, un cambio concreto o una acción con un responsable claro**. “Tenemos que comunicarnos mejor” difícilmente cumple con esa condición."
          },
          {
              "type": "image",
              "src": "/blog-images/18-c.jpeg",
              "alt": "Conversar no es resolver: una buena conversación termina en una decisión, un cambio o una acción con responsable"
          },
          {
              "type": "heading",
              "text": "Cuando un problema vuelve, entrega información"
          },
          {
              "type": "paragraph",
              "text": "Cuando un problema vuelve, además entrega información nueva: **la solución anterior no fue suficiente**. Puede que se haya identificado mal la causa, que la decisión haya sido correcta pero nunca se ejecutó, que nadie asumiera realmente la responsabilidad o que hayan aparecido antecedentes nuevos. En vez de empezar la discusión desde cero, conviene **revisar qué se decidió la última vez, qué efectivamente cambió y dónde falló la solución**. Esa disciplina evita que las reuniones se conviertan en una **repetición mensual de las mismas frustraciones**."
          },
          {
              "type": "heading",
              "text": "Identificar, Discutir y Resolver"
          },
          {
              "type": "paragraph",
              "text": "EOS aborda esto con una lógica deliberadamente simple: **Identificar, Discutir y Resolver (IDS)**. Primero se busca el problema real (la **causa raíz**), después se conversa solo lo necesario para entenderlo y finalmente se toma una decisión que permita avanzar. No elimina los problemas —una empresa que crece siempre tendrá nuevos desafíos—, pero sí ayuda a que **los problemas resueltos tiendan a mantenerse resueltos**. Como implementador, buena parte de mi trabajo consiste precisamente en ayudar a los equipos a desarrollar esa disciplina: **menos tiempo apagando el mismo incendio y más capacidad para resolver lo que realmente está frenando a la organización**."
          },
          {
              "type": "image",
              "src": "/blog-images/18-d.jpeg",
              "alt": "Identificar. Discutir. Resolver. La meta no es tener menos problemas, es dejar de resolver siempre los mismos"
          }
      ]
  },
  {
    number: 19,
    slug: "delegar-sin-desaparecer-autonomia-equipo",
    image: "/blog-images/19.jpeg",
    category: "Liderazgo",
    title: "Delegar sin desaparecer: cómo dar autonomía y seguir presente",
    highlight: "sin desaparecer",
    seoTitle: "Delegar sin desaparecer: cómo dar autonomía al equipo",
    excerpt:
      "Cómo delegar con claridad: define resultados, autoridad y seguimiento para que tu equipo decida sin que el líder desaparezca.",
    keywords: "delegar sin desaparecer, cómo delegar en una empresa, dar autonomía al equipo, seguimiento de la delegación, delegar responsabilidades, líder presente sin microgestión, EOS Accountability Chart GWC",
    date: "02 de noviembre, 2026",
    readTime: "4 min de lectura",
    content: `Carlos Garcín - Professional EOS Implementer para Chile & Latinoamérica
¿Te pasa algo parecido? Escríbeme y te cuento, sin compromiso, cómo funciona EOS y si puede hacer sentido para tu empresa.`,
    blocks: [
      {
        type: "paragraph",
        text: "**Delegar no es decir “encárgate” y desaparecer.** Para que funcione, la persona debe saber qué resultado se espera, qué puede decidir por su cuenta y cuándo volverán a revisar cómo va.",
      },
      {
        type: "quote",
        text: "El líder deja de meterse en cada paso, pero sigue disponible para destrabar lo que se complique.",
      },
      { type: "chips", items: ["Qué resultado se espera", "Qué puede decidir", "Cuándo se revisa el avance"] },
      { type: "image", src: "/blog-images/19-b.jpeg", alt: "Tres acuerdos para delegar bien: resultado esperado, margen para decidir y seguimiento acordado" },
      { type: "heading", text: "Lo que aprendió Clay Harris al crecer" },
      {
        type: "paragraph",
        text: "En [“Do It Scared: Clay Harris Learns to Let the Team Cook”](https://www.eosworldwide.com/blog/do-it-scared-clay-harris-learns-to-let-the-team-cook), artículo de EOS Worldwide escrito por Mark O’Donnell, Clay Harris relata cómo cambió su manera de liderar al crecer **WorkSmart IT**. El primer quiebre apareció cerca de los US$5 millones de ingresos y 30 empleados: el equipo necesitaba más dirección, las reuniones eran inconsistentes y las ideas de gestión que probaban no formaban un sistema.",
      },
      {
        type: "stat",
        value: "US$20 M+",
        label: "de ingresos y más de 100 personas",
        subtext: "WorkSmart IT hoy; el primer quiebre llegó con ~US$5 M y 30 empleados",
      },
      {
        type: "paragraph",
        text: "Con el crecimiento, Harris dejó de estar cerca de todo el trabajo y descubrió que **muchas veces su equipo tenía mejor información que él**. Le costó darles espacio para decidir y, después de haber [retenido demasiadas responsabilidades](/empresa-dependiente-del-fundador), reconoce que también llegó a apartarse demasiado.",
      },
      { type: "heading", text: "Un caso que vi de cerca: la mesa de ayuda" },
      {
        type: "paragraph",
        text: "Lo vi también al trabajar con un cliente que necesitaba ordenar su mesa de ayuda. Sus líderes técnicos estaban absorbiendo los requerimientos de soporte y eso les quitaba foco de los proyectos; además, el apoyo externo no estaba dando el resultado esperado. **No bastaba con pedirles que delegaran**: primero había que definir qué cubriría el servicio, quién lo atendería, por qué canales y con qué niveles de atención.",
      },
      {
        type: "cascade",
        levels: [
          { label: "Antes", desc: "“¿Quién puede tomar estos tickets?”" },
          { label: "Después", desc: "“¿Qué modelo de soporte necesita el negocio?”" },
          { label: "Para qué", desc: "Que los líderes técnicos se enfoquen en desarrollo" },
        ],
      },
      {
        type: "paragraph",
        text: "**Antes de delegar una responsabilidad, conviene acordar cinco cosas:**",
      },
      {
        type: "step",
        number: 1,
        name: "Resultado",
        title: "¿Qué debe quedar logrado?",
        body: "Acuerda el resultado esperado, no la lista de pasos para llegar a él.",
      },
      {
        type: "step",
        number: 2,
        name: "Decisiones",
        title: "¿Qué puede resolver la persona sin pedir permiso?",
        body: "Aquí se define la autoridad real. Lo que no queda claro como decisión propia termina [volviendo al dueño](/problemas-escalan-al-dueno).",
      },
      {
        type: "step",
        number: 3,
        name: "Límites",
        title: "¿Qué condiciones de presupuesto, calidad o riesgo debe respetar?",
        body: "Los límites dan seguridad a ambos: la persona sabe hasta dónde llega y el líder sabe cuándo corresponde intervenir.",
      },
      {
        type: "step",
        number: 4,
        name: "Apoyo",
        title: "¿Qué información, recursos o aprendizaje necesita?",
        body: "Delegar también es asegurar que la persona tenga con qué hacerse cargo.",
      },
      {
        type: "step",
        number: 5,
        name: "Seguimiento",
        title: "¿Cuándo revisarán el avance y qué debe escalar de inmediato?",
        body: "Es la parte que evita desaparecer: una fecha de revisión y criterios claros para escalar lo que se complique.",
      },
      { type: "heading", text: "Cómo EOS convierte estos acuerdos en rutina" },
      {
        type: "paragraph",
        text: "EOS ayuda a convertir esos acuerdos en parte de la operación: el [Accountability Chart](/organizacion-correcta-antes-de-completar-el-equipo) aclara quién responde por cada función; [GWC](/persona-correcta-puesto-correcto-gwc-eos) ayuda a revisar si la persona entiende el asiento, quiere ocuparlo y tiene capacidad; y las **Rocas**, el [Scorecard](/como-construir-un-tablero-de-indicadores) y las [reuniones de seguimiento](/reuniones-largas-sin-resultados) muestran si se avanza y dónde hay que resolver obstáculos.",
      },
      { type: "chips", items: ["Accountability Chart", "GWC", "Rocas", "Scorecard", "Reuniones de seguimiento"] },
      {
        type: "punchline",
        lines: [
          "El líder mantiene claridad sobre el resultado",
          "sin dictar cada paso.",
        ],
      },
      { type: "image", src: "/blog-images/19-c.jpeg", alt: "El equipo avanza y tú recuperas tiempo para dirigir: ¿qué decisión puede tomar tu equipo esta semana?" },
      {
        type: "callout",
        text: "Si en tu empresa algunas decisiones siguen volviendo siempre al dueño o a los mismos líderes, [conversemos](/contacto). Podemos revisar si hace falta delegar mejor, aclarar responsabilidades o rediseñar la operación.",
      },
    ],
  },
];
